import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    ApiReportAnalyticRatingParams,
    GroupService,
    OrganizationService,
    Period,
    ReferenceService
} from '../../../apiModule1';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatestAll, map} from 'rxjs/operators';
import {quarters, years} from '../../index-ias/filter-dialog/filter-dialog.component';
import {MultiSelect3Component} from '../../../../shared/components/multi-select3/multi-select3.component';
import {MultiSelect2Component} from "../../../../shared/components/multi-select2/multi-select2.component";
import {ToastsService} from "../../../../core/services/toasts.service";
import {PeriodColumnsService} from "../period-columns.service";
import {MatCheckbox} from "@angular/material/checkbox";
import {combineLatest} from "rxjs";

export function conditionalValidator(relatedControlName: string, compareFn: (value: any) => boolean, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.parent) {
            return null; // Если parent отсутствует (например, при инициализации)
        }

        const relatedControl = control.parent.get(relatedControlName);

        if (!relatedControl) {
            console.warn(`Control with name "${relatedControlName}" not found`);
            return null;
        }

        // Проверяем, является ли связанный контрол обязательным
        if (compareFn(relatedControl.value)) {
            const res = validator(control);
            console.log('res', res);
            return res; // Применяем валидатор, если контрол required
        }

        return null; // Валидатор не активен
    };
}

@Component({
    selector: 'pas-occupied-places-filter-dialog',
    standalone: true,
    imports: [
        MatButton,
        MatIcon,
        MatIconButton,
        ReactiveFormsModule,
        MultiSelect3Component,
        MultiSelect2Component,
        MatCheckbox,
    ],
    templateUrl: './report-occupied-places-filter-dialog.component.html',
    styleUrls: ['../../index-entity/filter-dialog/filter-dialog.component.scss', './report-occupied-places-filter-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportOccupiedPlacesFilterDialogComponent implements OnInit {
    dialogRef = inject(MatDialogRef<ReportOccupiedPlacesFilterDialogComponent>);
    toast = inject(ToastsService);
    referenceService = inject(ReferenceService);
    // analyticsService = inject(AnalyticService)
    destroyRef = inject(DestroyRef);

    periodService = inject(PeriodColumnsService)

    fb = inject(FormBuilder);
    cdr = inject(ChangeDetectorRef);
    groupService = inject(GroupService)
    organizationsService = inject(OrganizationService)
    data: Record<string, any> = inject<ApiReportAnalyticRatingParams>(MAT_DIALOG_DATA)

    years = years;
    quarters = quarters;

    archiveStatuses: Status[] = []

    form: FormGroup = this.fb.group({
        archive: new FormControl<Status[] | null>(null),
        organizationGroup: new FormControl<Group[] | null>(null),
        organizations: new FormControl<Organization[] | null>(null),
    });


    onNoClick() {
        this.dialogRef.close();
    }


    ngOnInit(): void {

        this.referenceService.apiReferenceStatusesGet()
            .pipe(map(v => v.values))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((v: Status[]) => {
                this.archiveStatuses = v;
                this.setFormData()
            })

    }

    setFormData(): void {

        combineLatest({
            groups: this.groupService.apiGroupGet({pageIndex: 0}).pipe(map(v => v.values)),
            organizationsData: this.organizationsService.apiOrganizationGet({pageIndex: 0}).pipe(map(v => v.values))
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({groups, organizationsData})=>{
                //groups
                if (!groups || !groups.length || !this.data) return
                const organizationGroup = this.data['OrganizationGroupIds'] && this.data['OrganizationGroupIds']
                    .map((v: number) => groups.find((g: Group) => g.id === v))

                this.data['Periods']?.forEach((v: Period) => {
                    this.periodService.addToSelectedPeriod(v)
                })

                //organizations
                if (!organizationsData || !organizationsData.length || !this.data) return
                const organizations = this.data['OrganizationIds'] && this.data['OrganizationIds']
                    .map((v: number) => organizationsData.find((g: Group) => g.id === v))

                //archive
                const archive = this.data?.['OrgStatusIds'] && this.data?.['OrgStatusIds']
                    .map((v: number) => this.archiveStatuses.find((s: Status) => s.id === v))

                this.form.patchValue({
                    organizationGroup,
                    organizations,
                    archive
                })

                //устанавливаем текущий фильтр для периода
                this.periodService.setFilters(this.formToPeriodFilter())

                //подписываемся на изменения фильтра
                this.form.valueChanges
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(v => {
                        const res: ApiReportAnalyticRatingParams = this.formToPeriodFilter()
                        this.periodService.setFilters(res)
                    })
            })


        this.cdr.detectChanges()
    }

    formToPeriodFilter(): ApiReportAnalyticRatingParams {
        const OrganizationGroupIdsValue = this.form.get('organizationGroup')?.value?.map((v: Group) => v.id)
        const OrganizationGroupIds = OrganizationGroupIdsValue?.length ? OrganizationGroupIdsValue : undefined

        const OrganizationIdsValue = this.form.get('organizations')?.value?.map((v: Group) => v.id)
        const OrganizationIds = OrganizationIdsValue?.length ? OrganizationIdsValue : undefined

        const OrgStatusIdsValue = this.form.get('archive')?.value?.map((v: Group) => v.id)
        const OrgStatusIds = OrgStatusIdsValue?.length ? OrgStatusIdsValue : undefined

        const res: ApiReportAnalyticRatingParams = {
            OrganizationGroupIds,
            OrganizationIds,
            OrgStatusIds,
        }
        return res
    }

    onApply() {
        if (this.form.invalid) return;
        const res: ApiReportAnalyticRatingParams = {
            OrganizationGroupIds: this.form.get('organizationGroup')?.value?.map((v: Group) => v.id),
            OrganizationIds: this.form.get('organizations')?.value?.map((v: Organization) => v.id),
            OrgStatusIds: this.form.get('archive')?.value?.map((v: Status) => v.id),
            Periods: this.periodService.getSelectedSignal()(),
        }
        this.dialogRef.close(res)
        // this.periodService.setFilters(res)
    }

    onReset() {
        this.form.reset();
    }
}
